import { createMemoryHistory } from "history";

/**
 * Creates a memory history for Sandbox that also notifies the portal
 * whenever navigation happens, so the portal can update the real URL.
 *
 * The callback receives (path, action, delta):
 *   - action "PUSH"    → sandbox pushed a new route
 *   - action "REPLACE" → sandbox replaced the current route
 *   - action "POP"     → sandbox navigated via goBack/goForward/go
 *   - delta            → only for POP: how many steps moved (e.g. -1 for goBack)
 *
 * @param {object} options
 * @param {string} options.initialPath - The sandbox route to start at (e.g. "/exercises")
 * @param {function} options.onSandboxNavigate - Called with (path, action, delta)
 * @returns {import("history").MemoryHistory} A memory history with intercepted navigation methods
 */
export function createEmbeddedHistory({ initialPath = "/", onSandboxNavigate } = {}) {
  const memHistory = createMemoryHistory({ initialEntries: [initialPath] });

  const originalPush = memHistory.push.bind(memHistory);
  const originalReplace = memHistory.replace.bind(memHistory);

  // Track the memory history index so we can compute delta on POP events.
  let lastIndex = memHistory.index;

  const toPathname = (path) =>
    typeof path === "string" ? path : path?.pathname ?? "/";

  memHistory.push = (path, state) => {
    originalPush(path, state);
    lastIndex = memHistory.index;
    onSandboxNavigate?.(toPathname(path), "PUSH");
  };

  memHistory.replace = (path, state) => {
    originalReplace(path, state);
    lastIndex = memHistory.index;
    onSandboxNavigate?.(toPathname(path), "REPLACE");
  };

  // Listen for POP events (triggered by go / goBack / goForward).
  // Compute the delta so the portal can call history.go(delta) to stay in sync.
  memHistory.listen((location, action) => {
    if (action === "POP") {
      const currentIndex = memHistory.index;
      const delta = currentIndex - lastIndex;
      lastIndex = currentIndex;
      onSandboxNavigate?.(location.pathname, "POP", delta);
    }
  });

  // Expose the unwrapped replace so SandboxRenderer can sync the memory
  // history on browser back/forward without triggering onSandboxNavigate
  // again (prevents echo loops).
  memHistory.__originalReplace = originalReplace;

  return memHistory;
}
